import React, { useMemo, useState } from "react";
import QRCode from "qrcode";
import { FaEdit, FaTrashAlt, FaInfoCircle, FaWhatsapp, FaPhone, FaEnvelope, FaShareAlt, FaRegCopy, FaQrcode, FaCheck, FaTimes, FaPlus } from "react-icons/fa";
import {
  formatPascal,
  formatPhone,
  formatEmail,
  formatServiceShortcode,
  getServiceShortcodeFromCategories
} from "../utils/formatters";

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

// Legacy clipboard copy for insecure (HTTP) contexts where navigator.clipboard is unavailable
function copyTextFallback(text) {
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

const getRealestateStatusTone = (status) => {
  const value = String(status || "").trim().toLowerCase();
  if (/(won|qualified|converted|completed|active)/.test(value)) return "success";
  if (/(lost|rejected|cancelled|canceled|closed)/.test(value)) return "danger";
  if (/(contacted|follow|pending|progress|warm)/.test(value)) return "warning";
  if (/(new|open|cold)/.test(value)) return "info";
  return "neutral";
};

/**
 * Shared table body renderer for Business Leads, Training Leads, and Customers.
 * The parent decides which rows to pass and what `activeTab` is.
 */
function LeadsTableBody({
  rows,
  activeTab,
  courses,
  domains,
  servicesData = [],
  getColumnWidth,
  getColumnMinWidth,
  handleView,
  handleEdit,
  handleDelete,
  onToggleReFlag,
  leadStatuses = [],
  onUpdateReStatus,
  onAddReFollowupNote,
  selectedReLeadIds,
  onToggleReLeadSelection
}) {
  const [addingNote, setAddingNote] = useState({ id: null, value: "" });
  const [savingNote, setSavingNote] = useState(false);
  const [followupNotesModal, setFollowupNotesModal] = useState({
    open: false,
    content: ""
  });
  const [propertyLinkModal, setPropertyLinkModal] = useState({
    open: false,
    url: "",
    title: ""
  });
  const [shareModal, setShareModal] = useState({
    open: false,
    title: "",
    text: ""
  });
  const [qrModal, setQrModal] = useState({
    open: false,
    dataUrl: "",
    title: "",
    number: ""
  });
  const safeRows = Array.isArray(rows) ? rows : [];
  const hasEdit = typeof handleEdit === "function";
  const hasDelete = typeof handleDelete === "function";

  const formatRowPhone = (row) => {
    if (!row?.phone || row.phone === "—") return "—";
    if (activeTab !== "Realestate Leads") return formatPhone(row.phone) || "—";
    const phoneDigits = String(row.phone).replace(/\D/g, "");
    const countryDigits = String(row.countryCode || "").replace(/\D/g, "");
    const value =
      countryDigits && phoneDigits.length <= 10
        ? `${countryDigits}${phoneDigits}`
        : phoneDigits;
    return formatPhone(value) || "—";
  };

  const handleShareRealestateLead = async (row) => {
    const shareName = [
      formatPascal(row.firstName || row.first_name),
      formatPascal(row.lastName || row.last_name),
    ].filter(Boolean).join(" ") || String(row.name || "").replace(/,\s*/g, " ").trim();
    const rawCountryCode = String(row.countryCode || "").trim();
    const shareCountryCode =
      rawCountryCode && !rawCountryCode.startsWith("+")
        ? `+${rawCountryCode}`
        : rawCountryCode;
    const title = `Real Estate Lead - ${shareName || `#${row.id}`}`;
    const text = [
      title,
      row.phone && row.phone !== "—"
        ? `Contact: ${[shareCountryCode, row.phone].filter(Boolean).join(" ")}`
        : "",
      row.email && row.email !== "—" ? `Email: ${row.email}` : "",
      row.servicePropertyName && row.servicePropertyName !== "—"
        ? `Property / Service: ${row.servicePropertyName}`
        : "",
      row.propertyType && row.propertyType !== "—" ? `Property Type: ${row.propertyType}` : "",
      row.requirementType && row.requirementType !== "—"
        ? `Requirement Type: ${row.requirementType}`
        : "",
      row.location && row.location !== "—" ? `Location: ${row.location}` : "",
    ].filter(Boolean).join("\n");

    // Always open our own share menu so it works identically on http and https.
    setShareModal({ open: true, title, text });
  };

  const closeShareModal = () => setShareModal({ open: false, title: "", text: "" });

  const startAddNote = (row) => setAddingNote({ id: row.id, value: "" });

  const cancelAddNote = () => setAddingNote({ id: null, value: "" });

  const saveAddNote = async (row) => {
    if (typeof onAddReFollowupNote !== "function") return;
    const value = addingNote.value;
    if (!value || value.trim() === "") return;
    setSavingNote(true);
    try {
      await onAddReFollowupNote(row, value);
      cancelAddNote();
    } finally {
      setSavingNote(false);
    }
  };

  const handleShowMobileQr = async (row) => {
    if (!row?.phone || row.phone === "—") return;
    const phoneDigits = String(row.phone).replace(/\D/g, "");
    const countryDigits = String(row.countryCode || "").replace(/[^\d+]/g, "");
    const telUri = `tel:${`${countryDigits}${phoneDigits}`.replace(/[^\d+]/g, "")}`;
    const shareName = [
      formatPascal(row.firstName || row.first_name),
      formatPascal(row.lastName || row.last_name),
    ].filter(Boolean).join(" ") || String(row.name || "").replace(/,\s*/g, " ").trim();
    try {
      const dataUrl = await QRCode.toDataURL(telUri, { width: 240, margin: 1 });
      setQrModal({
        open: true,
        dataUrl,
        title: shareName || "Lead",
        number: formatRowPhone(row),
      });
    } catch (error) {
      console.error("Error generating mobile QR code:", error);
      alert("Unable to generate QR code for this number.");
    }
  };

  const closeQrModal = () => setQrModal({ open: false, dataUrl: "", title: "", number: "" });

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: shareModal.title, text: shareModal.text });
      closeShareModal();
    } catch (error) {
      if (error?.name === "AbortError") return;
    }
  };

  const handleCopyShareText = async () => {
    const text = shareModal.text;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        alert("Lead details copied to clipboard.");
        return;
      } catch {
        /* fall through to legacy copy */
      }
    }
    if (copyTextFallback(text)) {
      alert("Lead details copied to clipboard.");
    } else {
      window.prompt("Copy these lead details:", text);
    }
  };

  const followupNotesByRow = useMemo(() => {
    const map = new Map();
    safeRows.forEach((r) => {
      const raw = r?.notes == null ? "" : String(r.notes);
      const normalized = raw.trim();
      const isEmpty = !normalized || normalized === "—";
      map.set(r.id, {
        text: isEmpty ? "—" : normalized,
        showInfo: !isEmpty && (normalized.length > 90 || normalized.includes("\n"))
      });
    });
    return map;
  }, [safeRows]);

  if (safeRows.length === 0) {
    return null;
  }

  /** Translate shared logical columns to their rendered positions. */
  const custCol = (n) => {
    if (activeTab === "Clients" && n >= 1) return n + 1;
    if (activeTab === "Realestate Leads") {
      return {
        1: 1, 2: 3, 3: 8, 4: 9, 5: 4, 6: 9,
        7: 12, 8: 13, 9: 14, 10: 15, 11: 16, 12: 17, 13: 17,
      }[n] ?? n;
    }
    return n;
  };

  return (
    <>
      {safeRows.map((row) => (
        <tr key={row.id}>
          <td
            className="col-sticky col-0"
            style={{ minWidth: getColumnMinWidth(0) || (activeTab === "Realestate Leads" ? "130px" : "90px"), width: getColumnWidth(0) }}
          >
            {activeTab === "Realestate Leads" && (
              <input
                type="checkbox"
                checked={selectedReLeadIds?.has(String(row.id)) || false}
                onChange={() => onToggleReLeadSelection?.(row.id)}
                onClick={(e) => e.stopPropagation()}
                title="Select lead for export"
                aria-label={`Select ${row.name || "lead"} for export`}
                style={{ marginRight: 8 }}
              />
            )}
            <span
              className="table-name-link"
              onClick={() => handleView(row.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleView(row.id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              {([
                formatPascal(String(row.firstName || row.first_name || "").trim()),
                formatPascal(String(row.lastName || row.last_name || "").trim()),
              ]
                .filter(Boolean)
                .join(" ") ||
                formatPascal(String(row.name || "").replace(/,\s*/g, " ").trim())) || (
                <span className="muted-text">—</span>
              )}
            </span>
          </td>
          {activeTab === "Clients" && (
            <td
              style={{ minWidth: getColumnMinWidth(1) || "110px", width: getColumnWidth(1) }}
              className={
                !(row.customerType || row.customer_type) || (row.customerType || row.customer_type) === "—"
                  ? "muted-text"
                  : ""
              }
            >
              {formatPascal(row.customerType || row.customer_type) || "—"}
            </td>
          )}
          <td
            style={{ minWidth: getColumnMinWidth(custCol(1)) || (activeTab === "Realestate Leads" ? "150px" : "80px"), width: getColumnWidth(custCol(1)) }}
            className={!row.phone || row.phone === "—" ? "muted-text" : ""}
          >
            {activeTab === "Realestate Leads" ? (
              <div className="re-contact-cell">
                <span>{formatRowPhone(row)}</span>
                {row.phone && row.phone !== "—" && (
                  <button
                    type="button"
                    className="re-contact-qr-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowMobileQr(row);
                    }}
                    title="Show dial QR"
                    aria-label="Show dial QR code"
                  >
                    <FaQrcode size={14} />
                  </button>
                )}
              </div>
            ) : (
              formatRowPhone(row)
            )}
          </td>
          {activeTab === "Realestate Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(2) || "140px", width: getColumnWidth(2) }}
              className={!row.email || row.email === "—" ? "muted-text" : ""}
            >
              {formatEmail(row.email) || "—"}
            </td>
          )}
          <td
            style={{ minWidth: getColumnMinWidth(custCol(2)) || "100px", width: getColumnWidth(custCol(2)) }}
            className={!row.location || row.location === "—" ? "muted-text" : ""}
          >
            {formatPascal(row.location) || "—"}
          </td>

          {activeTab === "Realestate Leads" && (
            <>
              <td
                style={{ minWidth: getColumnMinWidth(4) || "100px", width: getColumnWidth(4) }}
                className={!row.status || row.status === "—" ? "muted-text" : ""}
              >
                <select
                  className={`re-status-select re-status-select--${getRealestateStatusTone(row.status)}`}
                  value={row.statusId != null ? String(row.statusId) : ""}
                  onChange={(e) => onUpdateReStatus?.(row.id, e.target.value)}
                  disabled={typeof onUpdateReStatus !== "function"}
                  onClick={(e) => e.stopPropagation()}
                  title="Update status"
                >
                  {row.statusId == null && (
                    <option value="">{row.status || "Select status"}</option>
                  )}
                  {leadStatuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(5) || "170px", width: getColumnWidth(5) }}
                className={!row.customerName ? "muted-text" : ""}
              >
                {row.customerName || "—"}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(6) || "130px", width: getColumnWidth(6) }}
                className={
                  !row.servicePropertyName || row.servicePropertyName === "—"
                    ? "muted-text"
                    : ""
                }
              >
                {isHttpUrl(row.propertyServiceLink) &&
                row.servicePropertyName &&
                row.servicePropertyName !== "—" ? (
                  <span
                    className="table-name-link"
                    role="button"
                    tabIndex={0}
                    title="Preview property / service"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPropertyLinkModal({
                        open: true,
                        url: row.propertyServiceLink,
                        title: row.servicePropertyName
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPropertyLinkModal({
                          open: true,
                          url: row.propertyServiceLink,
                          title: row.servicePropertyName
                        });
                      }
                    }}
                  >
                    {row.servicePropertyName}
                  </span>
                ) : (
                  row.servicePropertyName || "—"
                )}
              </td>
              <td
                style={{ minWidth: getColumnMinWidth(18) || "120px", width: getColumnWidth(18) }}
                className={!row.propertyLocation || row.propertyLocation === "—" ? "muted-text" : ""}
              >
                {formatPascal(row.propertyLocation) || "—"}
              </td>
            </>
          )}

          {/* Property Type is immediately before Requirement Type for RE leads. */}
          {activeTab === "Realestate Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(7) || "100px", width: getColumnWidth(7) }}
              className={!row.propertyType || row.propertyType === "—" ? "muted-text" : ""}
            >
              {formatPascal(row.propertyType) || "—"}
            </td>
          )}

          {/* Services / Courses column */}
          <td
            className="col-services"
            style={{ minWidth: getColumnMinWidth(custCol(3)) || "120px", width: getColumnWidth(custCol(3)) }}
          >
            {activeTab === "Clients" ? (
              row.services && row.services !== "—" ? (
                (() => {
                  const arr = Array.isArray(row.services)
                    ? row.services
                    : row.services.split(", ").map((s) => s.trim()).filter(Boolean);
                  const fullNames = arr.join(", ");
                  const shortcodes = arr.map((s) =>
                    getServiceShortcodeFromCategories(s, servicesData)
                  );
                  return (
                    <span className="services-cell">
                      <FaInfoCircle className="services-info-icon" title={fullNames} />
                      {shortcodes.join(", ")}
                    </span>
                  );
                })()
              ) : (
                <span className="muted-text">—</span>
              )
            ) : activeTab === "Training Leads" ? (
              (() => {
                const tl = row?.training_lead || row?.trainingLead || row;
                const coursesArr = Array.isArray(row?.Courses)
                  ? row.Courses
                  : Array.isArray(tl?.Courses)
                  ? tl.Courses
                  : [];

                // Prefer structured Courses array when available
                if (coursesArr && coursesArr.length > 0) {
                  const fullNamesArr = [];
                  const shortArr = [];

                  coursesArr.forEach((c) => {
                    const code = c.Course_Code || c.course_code || "";
                    const name = c.Course_Name || c.course_name || "";
                    const full = `${code}${
                      code && name ? " - " : ""
                    }${name}`.trim();
                    if (full) fullNamesArr.push(full);
                    const shortCode = code || formatServiceShortcode(name);
                    if (shortCode) shortArr.push(shortCode);
                  });

                  if (shortArr.length > 0) {
                    return (
                      <span className="services-cell">
                        <FaInfoCircle
                          className="services-info-icon"
                          title={fullNamesArr.join(", ")}
                        />
                        {shortArr.join(", ")}
                      </span>
                    );
                  }
                }

                // Fallbacks: use any direct string fields
                const direct =
                  row.courseCode ||
                  row.courseName ||
                  row.course ||
                  row.services;

                if (direct && direct !== "—") {
                  const parts = String(direct)
                    .split(",")
                    .map((p) => p.trim())
                    .filter(Boolean);
                  const fullNames = parts.join(", ");
                  const shortcodes = parts.map((p) => {
                    const [maybeCode, maybeName] = p.split(" - ").map((x) => x.trim());
                    if (maybeCode && maybeName) {
                      return maybeCode;
                    }
                    return formatServiceShortcode(p);
                  });

                  return (
                    <span className="services-cell">
                      <FaInfoCircle
                        className="services-info-icon"
                        title={fullNames}
                      />
                      {shortcodes.join(", ")}
                    </span>
                  );
                }

                if (row.courseId && Array.isArray(courses)) {
                  const course = courses.find((c) => c.Course_Id === row.courseId);
                  if (course) {
                    const code = course.Course_Code || course.course_code || "";
                    const name = course.Course_Name || course.course_name || "";
                    const full = `${code}${
                      code && name ? " - " : ""
                    }${name}`.trim();
                    const shortCode = code || formatServiceShortcode(name);
                    if (shortCode) {
                      return (
                        <span className="services-cell">
                          <FaInfoCircle
                            className="services-info-icon"
                            title={full}
                          />
                          {shortCode}
                        </span>
                      );
                    }
                    if (full) return full;
                  }
                }
                return <span className="muted-text">—</span>;
              })()
            ) : activeTab === "Realestate Leads" ? (
              formatPascal(row.requirementType) || "—"
            ) : row.services && row.services !== "—" ? (
              (() => {
                const arr = Array.isArray(row.services)
                  ? row.services
                  : row.services.split(", ").map((s) => s.trim()).filter(Boolean);
                const fullNames = arr.join(", ");
                const shortcodes = arr.map((s) =>
                  getServiceShortcodeFromCategories(s, servicesData)
                );
                return (
                  <span className="services-cell">
                    <FaInfoCircle className="services-info-icon" title={fullNames} />
                    {shortcodes.join(", ")}
                  </span>
                );
              })()
            ) : (
              <span className="muted-text">—</span>
            )}
          </td>

          {/* Business (domain catalog) / Mode column */}
          {activeTab === "Training Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(custCol(4)) || "100px", width: getColumnWidth(custCol(4)) }}
              className={!row.mode || row.mode === "—" ? "muted-text" : ""}
            >
              {formatPascal(
                (() => {
                  const tl = row?.training_lead || row?.trainingLead || row;
                  const modesArr = Array.isArray(row?.Modes)
                    ? row.Modes
                    : Array.isArray(tl?.Modes)
                    ? tl.Modes
                    : [];
                  if (Array.isArray(row.mode)) return row.mode.join(", ");
                  const fromRow = row.mode || row.Mode || row.mode_name || row.modeName;
                  if (fromRow) return fromRow;
                  const names = modesArr.map((m) => m.Mode_Name || m.mode_name).filter(Boolean);
                  return names.length > 0 ? names.join(", ") : "";
                })()
              ) || "—"}
            </td>
          )}
          {activeTab !== "Training Leads" && activeTab !== "Job Leads" && activeTab !== "Internship Leads" && activeTab !== "Realestate Leads" && (
            (() => {
              let domainName = row.domain && row.domain !== "—" ? row.domain : "";
              if (
                (!domainName || domainName === "—") &&
                row.domainId &&
                Array.isArray(domains) &&
                domains.length > 0
              ) {
                const dom = domains.find(
                  (d) => d.id === row.domainId || String(d.id) === String(row.domainId)
                );
                if (dom && dom.name) {
                  domainName = dom.name;
                }
              }
              const displayDomain =
                domainName && domainName.trim() !== "" ? formatPascal(domainName) : "—";
              const isMuted = !displayDomain || displayDomain === "—";
              return (
                <td
                  style={{ minWidth: getColumnMinWidth(custCol(4)) || "100px", width: getColumnWidth(custCol(4)) }}
                  className={isMuted ? "muted-text" : ""}
                >
                  {displayDomain}
                </td>
              );
            })()
          )}

          {/* Status */}
          {activeTab !== "Realestate Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(custCol(5)) || "100px", width: getColumnWidth(custCol(5)) }}
              className={!row.status || row.status === "—" ? "muted-text" : ""}
            >
              {row.status || "—"}
            </td>
          )}

          {/* Quote Sent (not for training, internship, job, or realestate leads) */}
          {activeTab !== "Training Leads" && activeTab !== "Internship Leads" && activeTab !== "Job Leads" && activeTab !== "Realestate Leads" && (
            <td
              style={{ minWidth: getColumnMinWidth(custCol(6)) || "100px", width: getColumnWidth(custCol(6)) }}
            >
              {row.isQuoteSent || "No"}
            </td>
          )}

          {/* Site Visit (Realestate only) */}
          {activeTab === "Realestate Leads" && (
            <td style={{ minWidth: "90px", width: getColumnWidth(9), textAlign: "center" }}>
              <input
                type="checkbox"
                checked={!!row.siteVisit}
                disabled={typeof onToggleReFlag !== "function"}
                onChange={(e) => onToggleReFlag?.(row.id, "siteVisit", e.target.checked)}
                title="Site Visit"
              />
            </td>
          )}
          {activeTab === "Realestate Leads" && (
            <>
              <td style={{ minWidth: "100px", width: getColumnWidth(10), textAlign: "center" }}>
                <input
                  type="checkbox"
                  checked={!!row.isOpportunity}
                  disabled={typeof onToggleReFlag !== "function"}
                  onChange={(e) => onToggleReFlag?.(row.id, "isOpportunity", e.target.checked)}
                  title="Opportunity"
                />
              </td>
              <td style={{ minWidth: "100px", width: getColumnWidth(11), textAlign: "center" }}>
                <label className="automation-switch" title="Automation">
                  <input
                    type="checkbox"
                    checked={!!row.automationEnabled}
                    disabled={typeof onToggleReFlag !== "function"}
                    onChange={(e) => onToggleReFlag?.(row.id, "automationEnabled", e.target.checked)}
                  />
                  <span className="automation-switch-track" aria-hidden="true">
                    <span className="automation-switch-thumb" />
                  </span>
                </label>
              </td>
            </>
          )}

          {/* Customer Notes (customers only) */}
          {activeTab === "Clients" && (
            <td
              style={{ minWidth: getColumnMinWidth(custCol(7)) || "120px", width: getColumnWidth(custCol(7)) }}
              className={
                !(row.customerNotes ?? row.Notes) || (row.customerNotes ?? row.Notes) === "—"
                  ? "muted-text"
                  : ""
              }
              title={row.customerNotes ?? row.Notes}
            >
              {(row.customerNotes ?? row.Notes) || "—"}
            </td>
          )}

          {/* Follow-up & dates */}
          <td
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 6 : activeTab === "Clients" ? 8 : 7
                  )
                ) || "140px",
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 6 : activeTab === "Clients" ? 8 : 7)
              )
            }}
            className={!row.lastFollowupDate || row.lastFollowupDate === "—" ? "muted-text" : ""}
          >
            {row.lastFollowupDate || "—"}
          </td>
          <td
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 7 : activeTab === "Clients" ? 9 : 8
                  )
                ) || "150px",
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 7 : activeTab === "Clients" ? 9 : 8)
              )
            }}
            className={!row.notes || row.notes === "—" ? "muted-text" : ""}
          >
            {activeTab === "Clients" ? (
              (() => {
                const noteMeta = followupNotesByRow.get(row.id) || { text: "—", showInfo: false };
                return (
                  <div className="followup-notes-cell">
                    {noteMeta.showInfo && (
                      <button
                        type="button"
                        className="followup-notes-info-btn"
                        title="View full follow-up notes"
                        onClick={() => setFollowupNotesModal({ open: true, content: noteMeta.text })}
                      >
                        <FaInfoCircle />
                      </button>
                    )}
                    <span className="followup-notes-clamp" title={noteMeta.text}>
                      {noteMeta.text}
                    </span>
                  </div>
                );
              })()
            ) : activeTab === "Realestate Leads" && typeof onAddReFollowupNote === "function" ? (
              addingNote.id === row.id ? (
                <div className="followup-note-edit">
                  <textarea
                    className="followup-note-input"
                    value={addingNote.value}
                    onChange={(e) => setAddingNote({ id: row.id, value: e.target.value })}
                    rows={2}
                    autoFocus
                    placeholder="New follow-up note"
                  />
                  <div className="followup-note-edit-actions">
                    <button
                      type="button"
                      className="followup-note-save"
                      title="Add follow-up"
                      disabled={savingNote}
                      onClick={() => saveAddNote(row)}
                    >
                      <FaCheck />
                    </button>
                    <button
                      type="button"
                      className="followup-note-cancel"
                      title="Cancel"
                      disabled={savingNote}
                      onClick={cancelAddNote}
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="followup-note-view">
                  <span className="followup-notes-clamp" title={row.notes}>
                    {row.notes || "—"}
                  </span>
                  <button
                    type="button"
                    className="followup-note-edit-btn"
                    title="Add follow-up"
                    onClick={() => startAddNote(row)}
                  >
                    <FaPlus />
                  </button>
                </div>
              )
            ) : (
              row.notes || "—"
            )}
          </td>
          <td
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 8 : activeTab === "Clients" ? 10 : 9
                  )
                ) || "140px",
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 8 : activeTab === "Clients" ? 10 : 9)
              )
            }}
            className={
              activeTab === "Realestate Leads"
                ? (!row.createdBy || row.createdBy === "—" ? "muted-text" : "")
                : (!row.nextFollowupDate || row.nextFollowupDate === "—" ? "muted-text" : "")
            }
          >
            {activeTab === "Realestate Leads"
              ? (row.createdBy || "—")
              : (row.nextFollowupDate || "—")}
          </td>
          <td
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 9 : activeTab === "Clients" ? 11 : 10
                  )
                ) || "100px",
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 9 : activeTab === "Clients" ? 11 : 10)
              )
            }}
            className={!row.createdAt || row.createdAt === "—" ? "muted-text" : ""}
          >
            {row.createdAt || "—"}
          </td>
          <td
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 10 : activeTab === "Clients" ? 12 : 11
                  )
                ) || "100px",
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 10 : activeTab === "Clients" ? 12 : 11)
              )
            }}
            className={!row.source || row.source === "—" ? "muted-text" : ""}
          >
            {formatPascal(row.source) || "—"}
          </td>

          {/* Company name (not training leads) */}
          {activeTab !== "Training Leads" && activeTab !== "Realestate Leads" && (
            <td
              style={{
                minWidth:
                  getColumnMinWidth(custCol(activeTab === "Clients" ? 13 : 11)) || "120px",
                width: getColumnWidth(custCol(activeTab === "Clients" ? 13 : 11))
              }}
              className={!row.companyName || row.companyName === "—" ? "muted-text" : ""}
            >
              {formatPascal(row.companyName) || "—"}
            </td>
          )}

          {/* Email */}
          {activeTab !== "Realestate Leads" && (
            <td
              style={{
                minWidth:
                  getColumnMinWidth(
                    custCol(
                      activeTab === "Training Leads" ? 11 : activeTab === "Clients" ? 14 : 12
                    )
                  ) || "100px",
                width: getColumnWidth(
                  custCol(activeTab === "Training Leads" ? 11 : activeTab === "Clients" ? 14 : 12)
                )
              }}
              className={!row.email || row.email === "—" ? "muted-text" : ""}
            >
              {formatEmail(row.email) || "—"}
            </td>
          )}

          {/* Actions */}
          <td
            className="col-sticky col-action"
            style={{
              minWidth:
                getColumnMinWidth(
                  custCol(
                    activeTab === "Training Leads" ? 12 : activeTab === "Clients" ? 15 : 13
                  )
                ) || "120px",
              width: getColumnWidth(
                custCol(activeTab === "Training Leads" ? 12 : activeTab === "Clients" ? 15 : 13)
              )
            }}
          >
            <div className={`action-buttons ${activeTab === "Realestate Leads" ? "re-lead-actions" : ""}`}>
              {activeTab === "Realestate Leads" && (
                <>
                  {row.phone && row.phone !== "—" && (
                    <>
                      <a
                        className="re-lead-action re-lead-action--whatsapp"
                        href={`https://wa.me/${String(row.countryCode || "").replace(/\D/g, "")}${String(row.phone).replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="WhatsApp"
                        aria-label="Open WhatsApp"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaWhatsapp size={14} />
                      </a>
                      <a
                        className="re-lead-action re-lead-action--call"
                        href={`tel:${row.countryCode || ""}${row.phone}`}
                        title="Call"
                        aria-label="Call lead"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                      </a>
                    </>
                  )}
                  {row.email && row.email !== "—" && (
                    <a
                      className="re-lead-action re-lead-action--email"
                      href={`mailto:${row.email}`}
                      title="Email"
                      aria-label="Email lead"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaEnvelope size={12} />
                    </a>
                  )}
                  <button
                    type="button"
                    className="re-lead-action re-lead-action--share"
                    onClick={() => handleShareRealestateLead(row)}
                    title="Share"
                    aria-label="Share lead"
                  >
                    <FaShareAlt size={13} />
                  </button>
                </>
              )}
              {activeTab !== "Clients" && hasEdit && (
                <button
                  className={activeTab === "Realestate Leads" ? "re-lead-action re-lead-action--edit" : "btn-edit"}
                  onClick={() => handleEdit(row.id)}
                  style={activeTab === "Realestate Leads" ? undefined : {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px 6px",
                    minWidth: 28,
                    minHeight: 28
                  }}
                  title="Edit"
                  aria-label="Edit lead"
                >
                  <FaEdit size={14} />
                </button>
              )}
              {activeTab === "Clients" && (
                <>
                  {hasEdit && (
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(row.id)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "6px 8px",
                        minWidth: 32,
                        minHeight: 32
                      }}
                      title="Edit Client"
                    >
                      <FaEdit size={14} />
                    </button>
                  )}
                  {hasDelete && (
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(row.id)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "6px 8px",
                        minWidth: 32,
                        minHeight: 32
                      }}
                      title="Delete Client"
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      ))}
      {followupNotesModal.open && (
        <tr>
          <td colSpan={activeTab === "Clients" ? 17 : 16} className="followup-notes-modal-cell">
            <div className="followup-notes-modal-overlay" onClick={() => setFollowupNotesModal({ open: false, content: "" })}>
              <div className="followup-notes-modal" onClick={(e) => e.stopPropagation()}>
                <div className="followup-notes-modal-header">
                  <h3>Follow-up Notes</h3>
                  <button type="button" onClick={() => setFollowupNotesModal({ open: false, content: "" })}>
                    Close
                  </button>
                </div>
                <div className="followup-notes-modal-body">
                  {followupNotesModal.content}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {propertyLinkModal.open && (
        <tr>
          <td colSpan={activeTab === "Clients" ? 17 : 16} className="followup-notes-modal-cell">
            <div
              className="followup-notes-modal-overlay"
              onClick={() => setPropertyLinkModal({ open: false, url: "", title: "" })}
            >
              <div
                className="followup-notes-modal property-link-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="followup-notes-modal-header">
                  <h3>{propertyLinkModal.title || "Property / Service"}</h3>
                  <button
                    type="button"
                    onClick={() => setPropertyLinkModal({ open: false, url: "", title: "" })}
                  >
                    Close
                  </button>
                </div>
                <iframe
                  className="property-link-modal-frame"
                  src={propertyLinkModal.url}
                  title={propertyLinkModal.title || "Property / Service Preview"}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
      {qrModal.open && (
        <tr>
          <td colSpan={activeTab === "Clients" ? 17 : 19} className="followup-notes-modal-cell">
            <div className="followup-notes-modal-overlay" onClick={closeQrModal}>
              <div
                className="followup-notes-modal re-qr-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="followup-notes-modal-header">
                  <h3>{qrModal.title}</h3>
                  <button type="button" onClick={closeQrModal}>
                    Close
                  </button>
                </div>
                <div className="re-qr-modal-body">
                  {qrModal.dataUrl && (
                    <img src={qrModal.dataUrl} alt={`QR code to dial ${qrModal.number}`} />
                  )}
                  <p className="re-qr-modal-number">{qrModal.number}</p>
                  <p className="re-qr-modal-hint">Scan with a phone camera to dial this number.</p>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
      {shareModal.open && (
        <tr>
          <td colSpan={activeTab === "Clients" ? 17 : 16} className="followup-notes-modal-cell">
            <div className="followup-notes-modal-overlay" onClick={closeShareModal}>
              <div
                className="followup-notes-modal lead-share-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="followup-notes-modal-header">
                  <h3>Share link</h3>
                  <button type="button" onClick={closeShareModal}>
                    Close
                  </button>
                </div>
                <div className="lead-share-modal-body">
                  <p className="lead-share-title" title={shareModal.title}>
                    {shareModal.title}
                  </p>
                  <div className="lead-share-preview">{shareModal.text}</div>
                  <div className="lead-share-options">
                    <a
                      className="lead-share-option whatsapp"
                      href={`https://wa.me/?text=${encodeURIComponent(shareModal.text)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeShareModal}
                    >
                      <FaWhatsapp /> WhatsApp
                    </a>
                    <a
                      className="lead-share-option email"
                      href={`mailto:?subject=${encodeURIComponent(shareModal.title)}&body=${encodeURIComponent(shareModal.text)}`}
                      onClick={closeShareModal}
                    >
                      <FaEnvelope /> Email
                    </a>
                    <button
                      type="button"
                      className="lead-share-option copy"
                      onClick={handleCopyShareText}
                    >
                      <FaRegCopy /> Copy details
                    </button>
                    {typeof navigator !== "undefined" && navigator.share && (
                      <button
                        type="button"
                        className="lead-share-option more"
                        onClick={handleNativeShare}
                      >
                        <FaShareAlt /> More apps
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default LeadsTableBody;

